// src/components/audit/AuditStats.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./AuditStats.css";

// Module-level guards to dedupe calls per country
const statsCache = new Map();   // country -> data
const inflight   = new Map();   // country -> Promise

function kpiClass(val, goodHigh = true, t1 = 80, t2 = 60) {
  // goodHigh=true: higher is better (rates)
  // goodHigh=false: lower is better (incident % / index)
  if (!Number.isFinite(val)) return "kpi-neutral";
  if (goodHigh) {
    if (val >= t1) return "kpi-good";
    if (val >= t2) return "kpi-watch";
    return "kpi-risk";
  }
  if (val <= t1) return "kpi-good";
  if (val <= t2) return "kpi-watch";
  return "kpi-risk";
}

export default React.memo(function AuditStats({ country, onToast }) {
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!country) return;

    if (statsCache.has(country)) {
      setPayload(statsCache.get(country));
      return;
    }
    if (inflight.has(country)) {
      inflight.get(country).then(setPayload).catch(() => {});
      return;
    }

    const controller = new AbortController();
    const p = axios.get("/api/audit_stats.php", {
      params: { country },
      withCredentials: true,
      signal: controller.signal,
    })
      .then(res => {
        const data = res.data?.data || {};
        statsCache.set(country, data);
        return data;
      })
      .then(setPayload)
      .catch(e => {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          onToast?.("Failed to load stats", "danger");
        }
      })
      .finally(() => inflight.delete(country));

    inflight.set(country, p);
    return () => controller.abort();
  }, [country]);

  const computed = useMemo(() => {
    if (!payload) return null;

    const ov = payload.overall || {};
    const n  = payload.now || {};

    // Global excl. OK
    const totalAllNonComp    = Number(ov.total_all_noncomp ?? 0);
    const resolvedAllNonComp = Number(ov.resolved_all_noncomp ?? 0);
    const avgSeverityAll     = ov.avg_severity_all != null ? Number(ov.avg_severity_all) : null;

    // Open / non-resolved / SLA
    const totalNonComp   = Number(n.total_noncompliant ?? 0);
    const openNonComp    = Number(n.open_noncompliant ?? 0);
    const nonResolved    = Number(n.nonresolved_noncompliant ?? 0);
    const openCritical   = Number(n.open_critical ?? 0);
    const openHigh       = Number(n.open_high ?? 0);
    const pastDue90      = Number(n.past_due_90d ?? 0);
    const noDeadline     = Number(n.no_deadline_open ?? 0);
    const dueSoon30      = Number(n.due_soon_30 ?? 0);
    const weightedScore  = Number(n.weighted_open_score ?? 0);

    // New KPI: prevalence as percentage (all years)
    const chUnresolvedPct   = n.highcrit_unresolved_per100 != null ? Number(n.highcrit_unresolved_per100) : null; // per100 == percentage
    const chUnresolvedCount = Number(n.highcrit_unresolved_all ?? 0);
    const allFindingsCount  = Number(n.all_noncompliant_all ?? 0);

    // Rates
    const resolutionRate = totalAllNonComp ? Math.round((resolvedAllNonComp / totalAllNonComp) * 100) : 0;
    const openRate       = totalNonComp    ? Math.round((openNonComp      / totalNonComp)      * 100) : 0;
    const slaBreachRate  = nonResolved     ? Math.round(((pastDue90 + noDeadline) / nonResolved) * 100) : null;

    // Risk index — prefer weighted from backend
    const riskIndex = n.risk_index_weighted != null
      ? Number(n.risk_index_weighted)
      : n.risk_index != null
      ? Number(n.risk_index)
      : (nonResolved ? (weightedScore / nonResolved) : 0);

    return {
      resolutionRate, openRate, slaBreachRate, riskIndex,
      avgSeverityAll, openCritical, openHigh, openNonComp, totalNonComp,
      pastDue90, noDeadline, dueSoon30, totalAllNonComp, nonResolved,
      chUnresolvedPct, chUnresolvedCount, allFindingsCount
    };
  }, [payload]);

  if (!computed) return null;

  const {
    resolutionRate, openRate, slaBreachRate, riskIndex, avgSeverityAll,
    openCritical, openHigh, openNonComp, totalNonComp,
    pastDue90, noDeadline, dueSoon30, totalAllNonComp, nonResolved,
    chUnresolvedPct, chUnresolvedCount, allFindingsCount
  } = computed;

  return (
    <div className="audit-stats-card">
      <div className="kpi-row">
        {/* Resolution rate */}
        <div className={`kpi-tile ${kpiClass(resolutionRate, true)}`}>
          <div className="kpi-title">Resolution rate</div>
          <div className="kpi-value">{resolutionRate}%</div>
          <div className="kpi-sub">All years · {totalAllNonComp} findings (excl. OK)</div>
        </div>

        {/* Open rate */}
        <div className={`kpi-tile ${kpiClass(openRate, false, 20, 40)}`}>
          <div className="kpi-title">Open rate (excl. OK)</div>
          <div className="kpi-value">{openRate}%</div>
          <div className="kpi-sub">All years · {openNonComp}/{totalNonComp}</div>
        </div>

        {/* Risk index */}
        <div className={`kpi-tile ${kpiClass(riskIndex, false, 3, 5)}`}>
          <div className="kpi-title">Risk index</div>
          <div className="kpi-value">{riskIndex.toFixed(1)}</div>
          <div className="kpi-sub">All years · avg severity of non-resolved · {nonResolved} items</div>
        </div>

        {/* SLA */}
        <div className={`kpi-tile ${kpiClass(slaBreachRate ?? 0, false, 10, 30)}`}>
          <div className="kpi-title">Past due &gt; 90d or no deadline</div>
          <div className="kpi-value">{slaBreachRate != null ? `${slaBreachRate}%` : "N/A"}</div>
          <div className="kpi-sub">
            {pastDue90} past · {noDeadline} no deadline
            {dueSoon30 ? ` · ${dueSoon30} due ≤30d` : ""} · {nonResolved} non-resolved
          </div>
        </div>

        {/* Unresolved Critical/High as percentage of all findings */}
        <div className={`kpi-tile ${kpiClass(chUnresolvedPct ?? 0, false, 5, 10)}`}>
          <div className="kpi-title">Unresolved Critical/High (all findings)</div>
          <div className="kpi-value">
            {chUnresolvedPct != null ? `${chUnresolvedPct}%` : "N/A"}
          </div>
          <div className="kpi-sub">
            All years · {chUnresolvedCount} C+H · {allFindingsCount} total
          </div>
        </div>
      </div>
    </div>
  );
});
